import { Link } from "react-router-dom";
import { BookOpen, Clock } from "lucide-react";
import { articlePath, fmtDate, fmtDateTime, fmtTime, isToday, imgSrc } from "../../lib/api";
import { ARTICLE_TYPES } from "../../lib/brand";

export const DemoBadge = ({ show }) =>
  show ? (
    <span className="inline-block rounded-sm bg-amber-300 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-950" data-testid="demo-badge" title="Razvojni primjer – nije stvarna vijest">
      Demo
    </span>
  ) : null;

const TYPE_TONE = { analiza: "text-brandblue dark:text-sky-300", komentar: "text-violet-700 dark:text-violet-300", vodic: "text-emerald-700 dark:text-emerald-300", izvjestaj: "text-muted-foreground" };

export const Kicker = ({ a, showCategory = true }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
    {showCategory && <span className="kicker">{a.category?.name}</span>}
    {a.article_type && a.article_type !== "vijest" && (
      <span className={`text-[10.5px] font-bold uppercase tracking-[0.1em] ${TYPE_TONE[a.article_type]}`} data-testid="editorial-indicator">
        {showCategory && <span className="mr-2 text-rule" aria-hidden>|</span>}{ARTICLE_TYPES[a.article_type]}
      </span>
    )}
  </div>
);

export const When = ({ s }) => (
  <time dateTime={s} title={fmtDateTime(s)}>{isToday(s) ? `Danas, ${fmtTime(s)} h` : fmtDate(s)}</time>
);

export const Meta = ({ a, className = "" }) => (
  <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground ${className}`}>
    <When s={a.published_at} />
    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" aria-hidden />{a.reading_time} min</span>
  </div>
);

export const Img = ({ a, ratio = "aspect-[3/2]", eager, sizes = "(min-width:1024px) 33vw, 100vw", className = "" }) => (
  !a.hero_image_url ? <div className={`h-1 w-14 bg-pulse ${className}`} aria-hidden data-testid="card-text-first" /> :
  <div className={`relative overflow-hidden bg-surface ${ratio} ${className}`}>
    <img src={imgSrc(a.hero_image_url)} alt={a.hero_image_alt || ""} loading={eager ? "eager" : "lazy"} decoding="async" sizes={sizes}
      width={1600} height={1067} fetchpriority={eager ? "high" : undefined}
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.025]" />
  </div>
);

const Hero = ({ a, id }) => (
  <Link to={articlePath(a)} className="group block" data-testid={id}>
    <Img a={a} ratio="aspect-[3/2] sm:aspect-[16/9]" eager sizes="(min-width:1024px) 58vw, 100vw" />
    <div className="mt-4 max-w-[46rem] lg:mt-5">
      <Kicker a={a} />
      <h2 className="hl mt-2 text-[1.5rem] leading-[1.18] sm:text-[1.9rem] lg:text-[2.15rem] lg:leading-[1.13] xl:text-[2.35rem]"><span className="headline-link">{a.headline}</span></h2>
      {a.subheadline && <p className="dek mt-3 line-clamp-3 text-[0.98rem] leading-relaxed text-muted-foreground sm:text-[1.05rem]">{a.subheadline}</p>}
      <Meta a={a} className="mt-3" />
    </div>
  </Link>
);

const Feature = ({ a, id, side }) => (
  <Link to={articlePath(a)} className={`group grid gap-4 ${side ? "sm:grid-cols-2 sm:gap-6" : ""}`} data-testid={id}>
    <Img a={a} ratio="aspect-[3/2]" sizes="(min-width:1024px) 40vw, 100vw" />
    <div className={side ? "self-center" : ""}>
      <Kicker a={a} />
      <h3 className="hl mt-2 text-[1.25rem] leading-[1.22] lg:text-[1.5rem]"><span className="headline-link">{a.headline}</span></h3>
      {a.subheadline && <p className="dek mt-2 line-clamp-3 text-[0.95rem] text-muted-foreground">{a.subheadline}</p>}
      <Meta a={a} className="mt-3" />
    </div>
  </Link>
);

const Standard = ({ a, id }) => (
  <Link to={articlePath(a)} className="group block" data-testid={id}>
    <Img a={a} sizes="(min-width:1024px) 25vw, (min-width:640px) 50vw, 100vw" />
    <div className="mt-3"><Kicker a={a} /></div>
    <h3 className="hl mt-1.5 text-[1.06rem] leading-[1.28] lg:text-[1.12rem]"><span className="headline-link">{a.headline}</span></h3>
    <Meta a={a} className="mt-2" />
  </Link>
);

const Horizontal = ({ a, id }) => (
  <Link to={articlePath(a)} className="group flex gap-4 py-4" data-testid={id}>
    <div className="min-w-0 flex-1">
      <Kicker a={a} />
      <h3 className="hl mt-1 text-[1rem] leading-[1.28] sm:text-[1.05rem]"><span className="headline-link">{a.headline}</span></h3>
      <Meta a={a} className="mt-1.5" />
    </div>
    <Img a={a} ratio="aspect-[4/3]" sizes="128px" className="w-28 shrink-0 sm:w-32" />
  </Link>
);

const shortTime = (s) => {
  if (isToday(s)) return fmtTime(s);
  return fmtDate(s);
};

const Compact = ({ a, id }) => (
  <Link to={articlePath(a)} className="group grid grid-cols-[4.6rem_1fr] gap-3 py-3" data-testid={id}>
    <time dateTime={a.published_at} title={fmtDateTime(a.published_at)} className="pt-0.5 text-[11.5px] font-bold tabular-nums text-pulse">{shortTime(a.published_at)}</time>
    <div className="min-w-0">
      <span className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">{a.category?.name}</span>
      <h3 className="mt-0.5 break-words font-display text-[0.96rem] font-semibold leading-[1.3] text-ink" style={{ hyphens: "auto" }}><span className="headline-link">{a.headline}</span></h3>
    </div>
  </Link>
);

const Text = ({ a, id }) => (
  <Link to={articlePath(a)} className="group block border-t border-rule pt-4" data-testid={id}>
    <Kicker a={a} />
    <h3 className="hl mt-2 text-[1.1rem] leading-[1.28] lg:text-[1.18rem]"><span className="headline-link">{a.headline}</span></h3>
    {a.subheadline && <p className="dek mt-2 line-clamp-2 text-[0.92rem] text-muted-foreground">{a.subheadline}</p>}
    <Meta a={a} className="mt-2" />
  </Link>
);

const Guide = ({ a, id }) => (
  <Link to={articlePath(a)} className="group flex h-full flex-col bg-white/[0.04] p-5 ring-1 ring-white/10 transition-colors hover:bg-white/[0.08]" data-testid={id}>
    <span className="inline-flex items-center gap-2 text-[10.5px] font-extrabold uppercase tracking-[0.14em] text-pulse-light"><BookOpen className="h-3.5 w-3.5" /> {a.category?.name === "Vodiči" ? "Vodič" : a.category?.name}</span>
    <h3 className="mt-3 font-display text-[1.15rem] font-semibold leading-[1.3] text-white" style={{ textWrap: "balance" }}><span className="headline-link">{a.headline}</span></h3>
    {a.subheadline && <p className="mt-2 line-clamp-2 text-[0.9rem] text-white/70">{a.subheadline}</p>}
    <span className="mt-auto pt-4 text-[12px] text-white/60">{a.reading_time} min čitanja →</span>
  </Link>
);

const VARIANTS = { hero: Hero, feature: Feature, standard: Standard, horizontal: Horizontal, compact: Compact, text: Text, guide: Guide };

export const ArticleCard = ({ a, variant = "standard", testId, side }) => {
  const C = VARIANTS[variant] || Standard;
  return <C a={a} side={side} id={testId || `article-card-${a.slug}`} />;
};
